import React, { useState, useContext } from 'react';
import { Navbar, Button, Container, Nav, Offcanvas } from 'react-bootstrap';
import { List, ArrowDownCircleFill, Whatsapp } from 'react-bootstrap-icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import logo from '../../../utils/rms.png';
import { DeferredPromptContext } from '../../../contexts/DeferredPromptContext';
import { useTiendaConfig } from '../../../hooks/useTiendaConfig';
import getIcon from '../../../utils/getIcon';
import { useSucursal } from '../../../contexts/SucursalContext';

function NavbarUni() {
    const [menuOpen, setMenuOpen] = useState(false);
    const { socialLinks, whatsappLink } = useTiendaConfig();
    const { deferredPrompt, setDeferredPrompt } = useContext(DeferredPromptContext); // Usa el contexto
    const navigate = useNavigate();
    //Sucursal Provider
    const { idSucursal: contextIdSucursal } = useSucursal(); // Obtén el idSucursal del contexto
    const { idSucursal: paramIdSucursal } = useParams();

    // Usa el idSucursal de los parámetros de la URL o, como respaldo, el del contexto
    const idSucursal = paramIdSucursal || contextIdSucursal;

    // Si aún no hay un idSucursal válido, redirige a una sucursal predeterminada o muestra un error
    if (!idSucursal) {
        console.warn('No se encontró idSucursal. Redirigiendo a la sucursal predeterminada...');
        navigate('/1'); // Cambia "1" por el idSucursal predeterminado
    }

    const redirectToRoot = () => {
        if (!idSucursal) {
            console.error('No se puede redirigir porque idSucursal es undefined.');
            return;
        }
        navigate(`/${idSucursal}`);
    };

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const handleInstallClick = async () => {
        if (deferredPrompt && typeof deferredPrompt.prompt === 'function') {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            console.log(`El usuario seleccionó: ${outcome}`);
            setDeferredPrompt(null);
        }
    };

    // Esta función se asegura de que siempre obtengas una URL válida de WhatsApp
    const getWhatsappUrl = () => {
        // Verifica si whatsappLink existe y tiene una URL válida
        if (whatsappLink && typeof whatsappLink.url === 'string' && whatsappLink.url.trim() !== '') {
            return whatsappLink.url;
        }
        // Si no se encuentra un enlace válido, devuelve un fallback
        return '#'; // O un valor predeterminado que prefieras
    };

    const isActive = (path) => {
        const currentPath = window.location.pathname; // Obtén la ruta actual
        if (path === `/${idSucursal}` || (idSucursal === undefined && currentPath === '/')) {
            // Resaltar "Catálogo" para la URL base, cualquier categoría de productos o el directorio raíz "/"
            return currentPath === path || currentPath === '/' || currentPath.startsWith(`/${idSucursal}/Productos`);
        }
        // Resaltar "Sobre Nosotros" para esa URL específica
        if (path === `/${idSucursal}/SobreNosotros`) {
            return currentPath === path;
        }
        // Resaltar "Preguntas Frecuentes" para esa URL específica
        if (path === `/${idSucursal}/PreguntaFrecuentes`) {
            return currentPath === path;
        }
        // Resaltar "Catalogo" para esa URL específica
        if (path === `/${idSucursal}/Catalogo`) {
            return currentPath === path;
        }
        return false;
    };

    return (
        <>
            <Navbar expand='lg' className='bg-black fixed-top z-2'>
                <Container fluid>
                    {/* Logo siempre visible */}
                    <Navbar.Brand className='text-start'>
                        <img
                            src={logo}
                            width='50'
                            alt='reymidascaps'
                            style={{ objectFit: 'cover' }}
                        />
                    </Navbar.Brand>

                    {/* Navegación principal (pantallas grandes) */}
                    <Nav className='d-none d-lg-flex align-items-center'>
                        <Button
                            className={`nav-link text-light fw-semibold fs-6 ms-3 ${isActive(`/${idSucursal}`) ? 'active-nav-link' : ''}`}
                            onClick={redirectToRoot}
                        >
                            Inicio
                        </Button>
                        
                        <Link to={`/${idSucursal}/Catalogo`} className='text-decoration-none ms-3'>
                            <Button className={`nav-link text-light fw-semibold fs-6 ${isActive(`/${idSucursal}/Catalogo`) ? 'active-nav-link' : ''
                                }`}>
                                Catálogo
                            </Button>
                        </Link>

                        <Link to={`/${idSucursal}/SobreNosotros`} className='text-decoration-none ms-3'>
                            <Button className={`nav-link text-light fw-semibold fs-6 ${isActive(`/${idSucursal}/SobreNosotros`) ? 'active-nav-link' : ''
                                }`}>
                                Sobre Nosotros
                            </Button>
                        </Link>

                        <Link to={`/${idSucursal}/PreguntaFrecuentes`} className='text-decoration-none ms-3'>
                            <Button className={`nav-link text-light fw-semibold fs-6 ${isActive(`/${idSucursal}/PreguntaFrecuentes`) ? 'active-nav-link' : ''
                                }`}>
                                Preguntas Frecuentes
                            </Button>
                        </Link>
                    </Nav>
                    <div className='d-flex align-items-center ms-auto'>
                        {/* Botón del menú toggle */}
                        <Button
                            variant='link'
                            onClick={toggleMenu}
                            aria-controls='offcanvasNavbar'
                            className='text-light me-2 d-lg-none'
                        >
                            <List size={24} />
                        </Button>

                        {/* Botones de "Instalar App" y "Grupo Mayorista" */}
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                variant="danger"
                                className="d-flex align-items-center position-relative d-none d-lg-inline"
                                onClick={handleInstallClick}
                                disabled={!deferredPrompt || typeof deferredPrompt.prompt !== 'function'}
                                aria-label="Instalar aplicación" // Texto accesible para lectores de pantalla
                                title="Instalar la aplicación en tu dispositivo"
                            >
                                <ArrowDownCircleFill size={20} />
                                <span className='d-none d-lg-inline ms-2'>Crear Atajo</span>
                            </Button>

                            <Link to={`/${idSucursal}/PreguntaFrecuentes`} className='text-decoration-none d-inline d-lg-none'>
                                <Button variant='danger' className="d-flex align-items-center position-relative">
                                    Preguntas Frecuentes
                                </Button>
                            </Link>

                            <Button
                                variant="danger"
                                className="d-flex align-items-center position-relative d-none d-lg-inline" 
                                aria-label="Unirse al grupo mayorista en WhatsApp"
                                title="Unirse al grupo mayorista en WhatsApp"
                                as="a" // Convierte el botón en un enlace
                                href={getWhatsappUrl()} // Función que asegura que siempre se pase una cadena válida
                                target="_blank" // Abre el enlace en una nueva pestaña
                                rel="noopener noreferrer"
                            >
                                <Whatsapp size={20} className='d-none d-lg-inline' />
                                <span className='ms-0 ms-md-2'>Grupo Difusión</span>
                            </Button>
                        </div>
                    </div>

                </Container>
            </Navbar>

            <Offcanvas show={menuOpen} onHide={toggleMenu} placement="start" className="bg-black">
                <Offcanvas.Header closeButton closeVariant="white">
                    <Offcanvas.Title className="text-light">Menú</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Nav className="flex-column">
                        <Link to="/" className="text-decoration-none">
                            <Button
                                className="nav-link text-light fw-semibold fs-6 text-start w-100"
                                onClick={redirectToRoot}
                            >
                                Inicio
                            </Button>
                        </Link>

                        <Link to="/Catalogo" className="text-decoration-none">
                            <Button className="nav-link text-light fw-semibold fs-6 text-start w-100">
                                Catálogo
                            </Button>
                        </Link>

                        <Link to="/SobreNosotros" className="text-decoration-none">
                            <Button className="nav-link text-light fw-semibold fs-6 text-start w-100">
                                Sobre Nosotros
                            </Button>
                        </Link>

                        <Link to="/PreguntaFrecuentes" className="text-decoration-none">
                            <Button className="nav-link text-light fw-semibold fs-6 text-start w-100">
                                Preguntas Frecuentes
                            </Button>
                        </Link>
                    </Nav>

                    {/* Línea divisoria */}
                    <hr className="bg-light my-4" />

                    {/* Íconos de redes sociales */}
                    <div className="d-flex justify-content-around">
                        {socialLinks.map((link, index) => (
                            <a
                                key={index}
                                href={link.url}
                                rel='noreferrer noopener'
                                title={link.title}
                            >
                                <Button variant="link" className="text-danger">
                                    {getIcon(link.url)}
                                </Button>
                            </a>
                        ))}
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
}

export default NavbarUni;
